import type { ReactiveController } from 'lit';
import { PIPELINE } from '../internal/constants.js';
import type { GridHost, Keys } from '../internal/types.js';
import { getDisplayColumns } from '../internal/utils.js';

/**
 * Default pixel indentation per depth level for the tree group column.
 */
export const DEFAULT_TREE_INDENT = 20;

/** Sentinel value used to mark synthesized parents (those that exist only as a path segment, with no backing row). */
const SYNTHESIZED = Symbol('synthesized-tree-row');

/**
 * Internal tree node. Built from the flat data + `getDataPath` callback;
 * lives only for the duration of one pipeline pass.
 */
interface TreeNode<T extends object> {
  /** The backing row when one exists at this path, else `null` (synthesized parent). */
  data: T | null;
  /** Path segments from root to this node. */
  path: readonly string[];
  /** `path.join(' ')` — used as stable string identity for synthesized nodes. */
  key: string;
  depth: number;
  parent: TreeNode<T> | null;
  children: TreeNode<T>[];
}

/**
 * Tree metadata exposed to renderers. One entry per row that ends up in the
 * flat output.
 */
export interface TreeRowMeta {
  depth: number;
  hasChildren: boolean;
  /** Stable key for the row (path-joined string). Useful for keyed reconciliation. */
  key: string;
  /** Last path segment — used as the group-column label for synthesized parents. */
  label: string;
}

/**
 * Reactive controller backing tree (nested-row) data.
 *
 * @remarks
 * Tree mode keeps the data array flat. Each row's hierarchy is derived from a
 * user-supplied `getDataPath` callback (AG Grid's "tree data" pattern). The
 * controller:
 *
 *  1. Builds an in-memory tree from the post-sort, post-filter rows.
 *  2. Synthesizes parent nodes for any path level that lacks a backing row.
 *  3. Walks the tree honouring the current expansion set and emits a flat
 *     list of rows in display order.
 *  4. Records per-row metadata (depth, hasChildren) that the cell renderer
 *     reads to draw the chevron + indentation.
 *
 * Expansion state is split into two sets — `expanded` for rows that have
 * backing data (keyed by reference, so it survives sort/filter), and
 * `expandedKeys` for synthesized parents (keyed by their path string).
 */
export class TreeController<T extends object> implements ReactiveController {
  public expanded: Set<T> = new Set();
  public expandedKeys: Set<string> = new Set();

  #meta = new WeakMap<T, TreeRowMeta>();
  #syntheticMeta = new Map<string, TreeRowMeta>();
  /** Synthesized parent placeholder rows so consumers still get a `T`-shaped object. */
  #syntheticRows = new Map<string, T>();
  /** Whether `defaultExpanded` has been applied once for the current data set. */
  #defaultsApplied = false;
  /** Most recently built root node. Used to walk the full tree for expand-all. */
  #lastRoot: TreeNode<T> | null = null;

  constructor(protected host: GridHost<T>) {
    this.host.addController(this);
  }

  public hostConnected() {}

  /** Whether tree mode is enabled at the grid level. */
  public get enabled(): boolean {
    return Boolean(this.host.tree?.enabled && this.host.tree?.getDataPath);
  }

  /** The configured indentation, in pixels, per depth level. */
  public get childIndent(): number {
    return this.host.tree?.childIndent ?? DEFAULT_TREE_INDENT;
  }

  /**
   * The column key that displays the chevron + indentation. Resolves to the
   * configured `groupColumnKey`, falling back to the first visible data column.
   */
  public get groupColumnKey(): Keys<T> | undefined {
    const configured = this.host.tree?.groupColumnKey;
    if (configured) return configured;
    const displayed = getDisplayColumns(this.host.columns).find((column) => !column.hidden);
    return displayed?.key;
  }

  public isGroupColumn(key: Keys<T>): boolean {
    return this.enabled && this.groupColumnKey === key;
  }

  /**
   * Per-row metadata. Populated as a side effect of {@link process}; reading
   * before the first pipeline pass returns `undefined`.
   */
  public getMeta(row: T): TreeRowMeta | undefined {
    return this.#meta.get(row);
  }

  public getDepth(row: T): number {
    return this.#meta.get(row)?.depth ?? 0;
  }

  public hasChildren(row: T): boolean {
    return Boolean(this.#meta.get(row)?.hasChildren);
  }

  public isSynthesized(row: T): boolean {
    return (row as { [SYNTHESIZED]?: boolean })[SYNTHESIZED] === true;
  }

  /** Group-column label override for synthesized parent rows. */
  public getLabel(row: T): string | null {
    if (!this.isSynthesized(row)) return null;
    return this.#meta.get(row)?.label ?? null;
  }

  public isExpanded(row: T): boolean {
    if (this.isSynthesized(row)) {
      const key = this.#meta.get(row)?.key;
      return key ? this.expandedKeys.has(key) : false;
    }
    return this.expanded.has(row);
  }

  /** Toggles the expansion state of a tree row. */
  public async toggleRow(row: T): Promise<boolean> {
    if (!this.enabled) return false;
    if (!this.hasChildren(row)) return false;
    const wasExpanded = this.isExpanded(row);
    return wasExpanded ? this.collapseRow(row) : this.expandRow(row);
  }

  public async expandRow(row: T): Promise<boolean> {
    if (!this.enabled) return false;
    if (!this.hasChildren(row)) return false;
    if (this.isExpanded(row)) return true;
    return this.#commit((nextData, nextKeys) => {
      if (this.isSynthesized(row)) {
        const key = this.#meta.get(row)?.key;
        if (key) nextKeys.add(key);
      } else {
        nextData.add(row);
      }
    });
  }

  public async collapseRow(row: T): Promise<boolean> {
    if (!this.enabled) return false;
    if (!this.isExpanded(row)) return true;
    return this.#commit((nextData, nextKeys) => {
      if (this.isSynthesized(row)) {
        const key = this.#meta.get(row)?.key;
        if (key) nextKeys.delete(key);
      } else {
        nextData.delete(row);
      }
    });
  }

  /** Expands every parent in the current tree. */
  public async expandAll(): Promise<boolean> {
    if (!this.enabled) return false;
    const root = this.#lastRoot;
    if (!root) return false;
    return this.#commit((nextData, nextKeys) => {
      const visit = (node: TreeNode<T>) => {
        if (node !== root && node.children.length > 0) {
          if (node.data) nextData.add(node.data);
          else nextKeys.add(node.key);
        }
        for (const child of node.children) visit(child);
      };
      visit(root);
    });
  }

  /** Collapses every currently-expanded row. */
  public async collapseAll(): Promise<boolean> {
    if (!this.enabled) return false;
    if (this.expanded.size === 0 && this.expandedKeys.size === 0) return true;
    return this.#commit((nextData, nextKeys) => {
      nextData.clear();
      nextKeys.clear();
    });
  }

  /**
   * Pipeline entry point. Called by {@link DataOperationsController.apply}
   * after sort/filter/quickFilter. Builds the tree, applies the configured
   * defaults the first time we see this data, and returns rows in
   * display-order with any synthesized parents inserted.
   */
  public process(input: ReadonlyArray<T>): T[] {
    if (!this.enabled) return input as T[];

    const getDataPath = this.host.tree!.getDataPath;
    const root = this.#buildTree(input, getDataPath);
    this.#lastRoot = root;

    // Apply defaultExpanded once per data set.
    if (!this.#defaultsApplied) {
      this.#applyDefaults(root);
      this.#defaultsApplied = true;
    }

    this.#refreshMeta(root);
    return this.#flatten(root);
  }

  /**
   * Resets cached metadata + defaults flag so the next pipeline pass
   * re-applies `defaultExpanded`. Called from the data watcher when the
   * input array is replaced wholesale.
   */
  public resetForDataChange(): void {
    this.#defaultsApplied = false;
    // Don't clear `expanded`/`expandedKeys`; consumers may want them to
    // survive across pipeline passes. If a wholesale data swap orphans
    // references, those entries are simply ignored on the next pass.
  }

  // --- internals ---------------------------------------------------------

  #buildTree(rows: ReadonlyArray<T>, getDataPath: (row: T) => readonly string[]): TreeNode<T> {
    const root: TreeNode<T> = {
      data: null,
      path: [],
      key: '',
      depth: -1,
      parent: null,
      children: [],
    };
    const byKey = new Map<string, TreeNode<T>>();
    byKey.set('', root);

    for (const row of rows) {
      const fullPath = getDataPath(row);
      if (!fullPath || fullPath.length === 0) continue;

      let parent = root;
      for (let i = 0; i < fullPath.length; i++) {
        const sub = fullPath.slice(0, i + 1);
        const key = sub.join(' ');
        let node = byKey.get(key);
        if (!node) {
          node = {
            data: null,
            path: sub,
            key,
            depth: i,
            parent,
            children: [],
          };
          byKey.set(key, node);
          parent.children.push(node);
        }
        if (i === fullPath.length - 1) {
          // The terminal segment is the actual record. If we previously
          // saw a child path that synthesized this level, attach the data
          // now.
          node.data = row;
        }
        parent = node;
      }
    }

    return root;
  }

  #applyDefaults(root: TreeNode<T>): void {
    const setting = this.host.tree?.defaultExpanded;
    if (setting === undefined || setting === false) return;
    const expandToDepth = typeof setting === 'number' ? setting : Number.POSITIVE_INFINITY;
    const visit = (node: TreeNode<T>) => {
      if (node !== root && node.children.length > 0 && node.depth <= expandToDepth) {
        if (node.data) this.expanded.add(node.data);
        else this.expandedKeys.add(node.key);
      }
      for (const child of node.children) visit(child);
    };
    visit(root);
  }

  #refreshMeta(root: TreeNode<T>): void {
    // Recompute meta for every node in the tree, both synthesized and
    // backed. The metadata is fresh each pipeline pass since depth or
    // hasChildren may have changed (rows reordered, filter dropped a
    // descendant, etc.).
    const visit = (node: TreeNode<T>) => {
      if (node !== root) {
        const meta: TreeRowMeta = {
          depth: node.depth,
          hasChildren: node.children.length > 0,
          key: node.key,
          label: node.path[node.path.length - 1] ?? '',
        };
        if (node.data) {
          this.#meta.set(node.data, meta);
        } else {
          this.#syntheticMeta.set(node.key, meta);
        }
      }
      for (const child of node.children) visit(child);
    };
    visit(root);
  }

  #flatten(root: TreeNode<T>): T[] {
    const out: T[] = [];
    const walk = (node: TreeNode<T>) => {
      if (node !== root) {
        if (node.data) {
          out.push(node.data);
        } else {
          // Synthesize a `T`-shaped placeholder so callers don't trip on
          // `null` rows. The placeholder carries the SYNTHESIZED marker.
          let placeholder = this.#syntheticRows.get(node.key);
          if (!placeholder) {
            placeholder = Object.create(null) as T;
            Object.defineProperty(placeholder, SYNTHESIZED, {
              value: true,
              enumerable: false,
            });
            this.#syntheticRows.set(node.key, placeholder);
          }
          this.#meta.set(placeholder, this.#syntheticMeta.get(node.key)!);
          out.push(placeholder);
        }
      }
      const expanded =
        node === root ||
        (node.data ? this.expanded.has(node.data) : this.expandedKeys.has(node.key));
      if (expanded) {
        for (const child of node.children) walk(child);
      }
    };
    walk(root);
    return out;
  }

  async #commit(mutate: (nextData: Set<T>, nextKeys: Set<string>) => void): Promise<boolean> {
    const nextData = new Set(this.expanded);
    const nextKeys = new Set(this.expandedKeys);
    mutate(nextData, nextKeys);

    const dataChanged =
      nextData.size !== this.expanded.size || [...nextData].some((row) => !this.expanded.has(row));
    const keysChanged =
      nextKeys.size !== this.expandedKeys.size ||
      [...nextKeys].some((key) => !this.expandedKeys.has(key));
    if (!dataChanged && !keysChanged) return true;

    const added: T[] = [];
    const removed: T[] = [];
    for (const row of nextData) if (!this.expanded.has(row)) added.push(row);
    for (const row of this.expanded) if (!nextData.has(row)) removed.push(row);

    const proceed = this.host.emitEvent('treeRowExpanding', {
      detail: {
        added,
        removed,
        current: Array.from(this.expanded),
        next: Array.from(nextData),
      },
      cancelable: true,
    });
    if (!proceed) return false;

    this.expanded = nextData;
    this.expandedKeys = nextKeys;
    // Re-run the pipeline so the flat output reflects the new expansion set.
    this.host.requestUpdate(PIPELINE);

    this.host.emitEvent('treeRowExpanded', {
      detail: { added, removed, expanded: Array.from(this.expanded) },
    });
    return true;
  }
}
